import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import LiveStrip from "./LiveStrip";
import FloatingSupportChat from "./FloatingSupportChat";
import HeathrowLiveStatusCompact from "./HeathrowLiveStatusCompact";
import CcgPremiumReviews from "./CcgPremiumReviews";
import PremiumContactStrip from "./PremiumContactStrip";
import PostcodeInput from "./PostcodeInput";
import { RouteIntelligencePreview2, RoutePricePreview2 } from "./RoutePreview2";
import LegolandMinibusLiveSummary from "./legoland/LegolandMinibusLiveSummary";
import api from "../utils/api";
import { trackFunnelEvent } from "../utils/funnelTracking";

const defaultHeroBenefits = [
  { icon: "£", title: "Fixed prices", body: "No hidden fees" },
  { icon: "○", title: "Free waiting", body: "60 minutes" },
  { icon: "✈", title: "Flight monitoring", body: "We track your flight" },
  { icon: "☎", title: "24/7 support", body: "Always here" },
];

const GROUP_GOOGLE_REVIEW_URL = "https://share.google/trIAb3GAvw55L1l8S";
const defaultRouteTrustCards = [
  {
    eyebrow: "GOV.UK registered operator",
    title: "Licensed & insured",
    body: "PSV licence PF2071924",
  },
  {
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "View our reviews",
    href: GROUP_GOOGLE_REVIEW_URL,
  },
];

const fallbackHeathrowLive = {
  code: "LHR",
  name: "Heathrow",
  overall: { label: "Checking", reason: "Live Heathrow route status is updating." },
  hero_status: {
    road: { label: "Checking", headline: "M4 and local road checks are updating." },
    rail: { label: "Checking", headline: "Elizabeth line and rail checks are updating." },
    flights: { label: "Checking", headline: "Flight disruption checks are updating." },
    weather: { label: "Checking", headline: "Weather checks are updating." },
  },
  summary: { headline: "Live Heathrow sources are checking." },
  preview: {
    terminal: "Terminals checking",
    terminal_pressure: [],
    terminal_queues: [],
    queues: ["Security and border queue checks are updating."],
    weather: "Weather checking",
  },
};

const statusToneKey = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("critical") || text.includes("severe") || text.includes("high") || text.includes("red") || text.includes("poor")) return "red";
  if (text.includes("moderate") || text.includes("busy") || text.includes("amber") || text.includes("watch") || text.includes("delay")) return "amber";
  if (text.includes("good") || text.includes("green") || text.includes("clear") || text.includes("normal") || text.includes("on time")) return "green";
  return "checking";
};

const cleanLiveText = (value, fallback = "Live status updating") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 86 ? `${text.slice(0, 83).trim()}...` : text;
};

const statusRank = (value) => {
  const tone = statusToneKey(value);
  if (tone === "red") return 4;
  if (tone === "amber") return 3;
  if (tone === "green") return 2;
  return 1;
};

const deriveTerminalLiveStatus = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : [];
  const strongest = structured
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: `${strongest.terminal || "Terminal"} pressure`,
    };
  }
  const text = String(preview.terminal || "");
  if (/severe|red|poor|high/i.test(text)) return { label: "High", detail: cleanLiveText(text, "Terminal pressure elevated") };
  if (/busy|moderate|amber|pressure/i.test(text)) return { label: "Moderate", detail: cleanLiveText(text, "Terminal pressure moderate") };
  if (/good|green|normal/i.test(text)) return { label: "Good", detail: cleanLiveText(text, "Terminal flow normal") };
  return { label: "Checking", detail: "Terminal pressure updating" };
};

const deriveQueueLiveStatus = (preview = {}) => {
  const queues = Array.isArray(preview.terminal_queues) ? preview.terminal_queues : [];
  const strongest = queues
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: `${strongest.terminal || "Queue"} waits updating`,
    };
  }
  const firstQueue = Array.isArray(preview.queues) ? preview.queues.find(Boolean) : "";
  if (/severe|red|45|60|busy/i.test(firstQueue)) return { label: "Busy", detail: cleanLiveText(firstQueue, "Queues busy") };
  if (/30|moderate|amber/i.test(firstQueue)) return { label: "Moderate", detail: cleanLiveText(firstQueue, "Queues moderate") };
  if (/good|green|0-5|5-10/i.test(firstQueue)) return { label: "Good", detail: cleanLiveText(firstQueue, "Queues look good") };
  return { label: "Checking", detail: cleanLiveText(firstQueue, "Security and border queues updating") };
};

const formatLiveAge = (value) => {
  if (!value) return "Auto-updated every 2 min";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Auto-updated every 2 min";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 90) return "Just updated";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min old`;
  return `${Math.round(seconds / 3600)} hr old`;
};

const buildGroupRouteLiveCards = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  const terminals = deriveTerminalLiveStatus(preview);
  const queues = deriveQueueLiveStatus(preview);

  return [
    { key: "road", icon: "▰", label: "M4 / A4 roads", value: hero.road?.label || "Checking", detail: hero.road?.headline || hero.road?.reason || "M4 and local road checks updating" },
    { key: "flights", icon: "✈", label: "Flights", value: hero.flights?.label || "Checking", detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating" },
    { key: "terminals", icon: "▦", label: "Terminals", value: terminals.label, detail: terminals.detail },
    { key: "queues", icon: "◫", label: "Queues", value: queues.label, detail: queues.detail },
  ];
};

const queueWaitRank = (wait = "") => {
  const text = String(wait || "").toLowerCase();
  const ranges = [...text.matchAll(/(\d+)\s*-\s*(\d+)/g)].map((match) => Number(match[2]));
  const singles = [...text.matchAll(/(?:^|[^\d])(\d+)(?:\s*\+|\s*min|$)/g)].map((match) => Number(match[1]));
  const lessThan = text.match(/<\s*(\d+)/);
  if (ranges.length) return Math.max(...ranges);
  if (singles.length) return Math.max(...singles);
  if (lessThan) return Number(lessThan[1]) - 1;
  return 0;
};

const queueLineText = (line = {}) => `${line.label || "Queue"}: ${line.wait || "checking"}`;

const formatQueueLines = (lines = []) => {
  const cleanLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  if (!cleanLines.length) return "Security and border snapshot";
  const sorted = cleanLines
    .slice()
    .sort((a, b) => queueWaitRank(b.wait) - queueWaitRank(a.wait));
  const worst = sorted[0];
  const rest = cleanLines.filter((line) => line !== worst);
  return [worst, ...rest].map(queueLineText).join(" • ");
};

const asLiveList = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return fallback.filter(Boolean);
};

const liveTextItem = (value, fallbackStatus = "Checking") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const parts = text.split(" • ").map((part) => part.trim()).filter(Boolean);
  const status = parts.length > 1 ? parts[parts.length - 1] : fallbackStatus;
  return {
    title: parts[0] || text || "Status checking",
    detail: parts.length > 2 ? parts.slice(1, -1).join(" • ") : "",
    status: /good|clear|on time|moderate|busy|watch|severe|high|delay|checking/i.test(status) ? status : fallbackStatus,
  };
};

const terminalPressureItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Terminal",
      detail: `${Number(item.flights || 0)} flights • ${Number(item.delayed_30 || 0)} delayed 30m+`,
      status: item.label || item.level || "Checking",
    }));
  }
  return String(preview.terminal || "Terminals checking")
    .split("•")
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const terminalQueueItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_queues) ? preview.terminal_queues.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Queue",
      detail: formatQueueLines(item.lines || []),
      status: item.label || item.level || "Checking",
    }));
  }
  return asLiveList(preview.queues, ["Security and border queue checks are updating."])
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const detailedHeathrowGroups = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  return [
    {
      key: "road",
      title: "Road corridors",
      subtitle: "M4, A4 and local Heathrow approach pressure",
      icon: "▰",
      status: hero.road?.label || "Checking",
      items: asLiveList(preview.road, [hero.road?.headline || hero.road?.reason || "M4 and local road checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.road?.label || "Checking")),
    },
    {
      key: "flights",
      title: "Flights",
      subtitle: "Disruption, arrivals and departures",
      icon: "✈",
      status: hero.flights?.label || "Checking",
      items: [
        {
          title: "Current flight pressure",
          detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating",
          status: hero.flights?.label || "Checking",
        },
        {
          title: "Airport flow",
          detail: `${preview.arrivals || "0"} arrivals • ${preview.departures || "0"} departures`,
          status: hero.flights?.label || "Checking",
        },
        ...asLiveList(preview.flights).slice(0, 1).map((item) => liveTextItem(item, hero.flights?.label || "Checking")),
      ],
    },
    {
      key: "rail",
      title: "Rail and Underground",
      subtitle: "Elizabeth line, Heathrow Express and airport stations",
      icon: "▤",
      status: hero.rail?.label || "Checking",
      items: asLiveList(preview.rail, [hero.rail?.headline || hero.rail?.reason || "Rail checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.rail?.label || "Checking")),
    },
    {
      key: "terminals",
      title: "Terminal pressure",
      subtitle: "Flight delay and cancellation status by terminal",
      icon: "▦",
      status: deriveTerminalLiveStatus(preview).label,
      items: terminalPressureItems(preview),
    },
    {
      key: "queues",
      title: "Queue snapshot",
      subtitle: "Security and border waits by terminal",
      icon: "◫",
      status: deriveQueueLiveStatus(preview).label,
      items: terminalQueueItems(preview),
    },
    {
      key: "weather",
      title: "Weather at LHR",
      subtitle: "Weather included in route timing checks",
      icon: "☁",
      status: hero.weather?.label || "Checking",
      items: [
        {
          title: preview.weather || "Weather checking",
          detail: hero.weather?.headline || hero.weather?.reason || "Live weather checks updating",
          status: hero.weather?.label || "Checking",
        },
      ],
    },
  ];
};

const localDatePartsInHours = (hours) => {
  const date = new Date(Date.now() + hours * 60 * 60 * 1000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  const [day, time] = date.toISOString().split("T");
  return { date: day, time: time.slice(0, 5) };
};

const extractPostcode = (text = "") => {
  const upper = String(text || "").toUpperCase();
  const match = upper.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/);
  if (match) return match[0].replace(/\s+/g, "");
  if (upper.includes("HEATHROW") || upper.includes("LHR")) return "TW6";
  return String(text || "").trim();
};

const normalizeHeathrowPlace = (value = "") => {
  const upper = String(value || "").toUpperCase();
  if (upper.includes("HEATHROW TERMINAL 5")) return "Heathrow Terminal 5";
  if (upper.includes("HEATHROW TERMINAL 4")) return "Heathrow Terminal 4";
  if (upper.includes("HEATHROW TERMINAL 3")) return "Heathrow Terminal 3";
  if (upper.includes("HEATHROW TERMINAL 2")) return "Heathrow Terminal 2";
  if (upper.includes("HEATHROW")) return value || "Heathrow Airport";
  return value;
};

const openSupportChat = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("ccg:open-support-chat", { detail: { flow: "enquiry" } }));
};

const PremiumLocationField = ({ ariaLabel, value, onChange, onPlaceSelected, placeholder, autoComplete }) => (
  <label className="minibus-v2-field minibus-v2-field-wide minibus-v2-location-field">
    <PostcodeInput
      value={value}
      onChange={onChange}
      onPlaceSelected={onPlaceSelected}
      placeholder={placeholder}
      restrictions={{ country: ["gb"] }}
      className="minibus-v2-location-input"
      style={{
        marginBottom: 0,
        height: "auto",
        minHeight: 0,
        padding: 0,
        border: 0,
        borderRadius: 0,
        background: "transparent",
        boxShadow: "none",
      }}
      inputProps={{
        "aria-label": ariaLabel,
        autoComplete,
      }}
    />
  </label>
);

export const GroupRouteQuoteForm = ({ config, route }) => {
  const navigate = useNavigate();
  const initialDateTime = useMemo(() => localDatePartsInHours(25), []);
  const minDateTime = useMemo(() => localDatePartsInHours(4), []);
  const [pickup, setPickup] = useState(config.pickupDefault ?? route.quote.pickupDefault);
  const [dropoff, setDropoff] = useState(config.dropoffDefault ?? route.quote.dropoffDefault);
  const [date, setDate] = useState(initialDateTime.date);
  const [time, setTime] = useState(initialDateTime.time);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pickupTime = `${date}T${time}`;
  const minPickupTime = `${minDateTime.date}T${minDateTime.time}`;

  const enforceMinimumPickupTime = (nextDate, nextTime) => {
    const nextPickupTime = `${nextDate}T${nextTime}`;
    if (nextPickupTime < minPickupTime) {
      setDate(minDateTime.date);
      setTime(minDateTime.time);
      return;
    }
    setDate(nextDate);
    setTime(nextTime);
  };

  const getQuote = async () => {
    if (!pickup.trim() || !dropoff.trim()) {
      setError("Please enter both pickup and drop-off details.");
      return;
    }
    if (route.quote.validatePickup && !route.quote.validatePickup(pickup)) {
      setError(route.quote.pickupError || "Please enter a pickup covered by this route page.");
      return;
    }
    if (route.quote.validateDropoff && !route.quote.validateDropoff(dropoff)) {
      setError(route.quote.dropoffError || "Please enter a destination covered by this route page.");
      return;
    }
    if (!date || !time || pickupTime < minPickupTime) {
      setError("Please choose a pickup time at least 4 hours from now.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup: extractPostcode(pickup),
        dropoff: extractPostcode(dropoff),
        pickup_time: pickupTime,
        passengers: config.passengers,
        bags: config.bags,
      });

      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        navigate(`${config.resultPath}/${data.quote_id}/${data.access_token}?${config.resultQuery}`);
        return;
      }
      setError(data.error || route.quote.genericError || "We could not calculate a quote for this journey. Please check the details and try again.");
    } catch (err) {
      setError(err.response?.data?.error || "Our quote system is temporarily busy. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="minibus-v2-quote-card" id={route.quote.formId}>
      <div className="minibus-v2-quote-head">
        <span>{config.formHeading ?? config.quoteLabel}</span>
        <small><i /> Free • Fast • No obligation</small>
      </div>

      <div className="minibus-v2-quote-grid">
        <PremiumLocationField
          ariaLabel={config.pickupAriaLabel ?? route.quote.pickupAriaLabel}
          value={pickup}
          onChange={setPickup}
          onPlaceSelected={(place) => setPickup(normalizeHeathrowPlace(place?.formatted_address || place?.name || ""))}
          placeholder={config.pickupPlaceholder ?? route.quote.pickupPlaceholder}
          autoComplete="street-address"
        />

        <PremiumLocationField
          ariaLabel={config.dropoffAriaLabel ?? route.quote.dropoffAriaLabel}
          value={dropoff}
          onChange={setDropoff}
          onPlaceSelected={(place) => setDropoff(normalizeHeathrowPlace(place?.formatted_address || place?.name || ""))}
          placeholder={config.dropoffPlaceholder ?? route.quote.dropoffPlaceholder}
          autoComplete="street-address"
        />

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup date</span>
          <input
            aria-label="Pickup date"
            type="date"
            value={date}
            min={minDateTime.date}
            onChange={(event) => enforceMinimumPickupTime(event.target.value, time)}
          />
        </label>

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup time</span>
          <input
            aria-label="Pickup time"
            type="time"
            value={time}
            onChange={(event) => enforceMinimumPickupTime(date, event.target.value)}
          />
        </label>

        <div className="minibus-v2-quote-actions">
          <button type="button" className="minibus-v2-quote-btn" onClick={getQuote} disabled={loading}>
            {loading ? "Checking..." : "Get quotes now"} <span>→</span>
          </button>
          <button type="button" className="minibus-v2-clear-btn" onClick={openSupportChat}>
            Need help?
          </button>
          <small>Takes 20 seconds</small>
        </div>
      </div>

      {error ? <div className="minibus-v2-error">{error}</div> : null}

      <div className="minibus-v2-quote-promises">
        {(route.quotePromises || [
          "Best price guarantee",
          "No hidden charges",
          "Free 60-min waiting",
          "Secure online booking",
          "Instant email confirmation",
        ]).map((promise) => <span key={promise}>{promise}</span>)}
      </div>

      <PremiumContactStrip title={`Need help with your ${config.vehicle} quote?`} />
    </div>
  );
};

export const GroupRouteSeo = ({ config, route, canonical }) => {
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: route.seo?.serviceName || `${config.brand} - ${route.seo?.routeName || config.h1}`,
        description: config.description,
        url: canonical,
        provider: { "@type": "Organization", name: config.brand, url: config.domain },
        areaServed: (route.seo?.areaServed || route.areas || []).map((name) => ({ "@type": "Place", name })),
        serviceType: route.seo?.serviceType || `${route.seo?.routeName || config.h1} ${config.vehicle} hire`,
        offers: { "@type": "Offer", priceCurrency: "GBP", url: canonical, availability: "https://schema.org/InStock" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: config.domain },
          { "@type": "ListItem", position: 2, name: route.seo?.routeName || config.h1, item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: (route.faqItems || []).map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  }), [canonical, config, route]);

  useEffect(() => {
    const applySeo = () => {
      const setMeta = (selector, attrs) => {
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement("meta");
          document.head.appendChild(el);
        }
        Object.entries(attrs).forEach(([key, value]) => {
          el[key] = value;
          if (typeof el.setAttribute === "function") el.setAttribute(key, value);
        });
      };

      document.title = config.title;
      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
      setMeta('meta[name="description"]', { name: "description", content: config.description });
      setMeta('meta[name="keywords"]', { name: "keywords", content: config.keywords });
      setMeta('meta[property="og:title"]', { property: "og:title", content: config.title });
      setMeta('meta[property="og:description"]', { property: "og:description", content: config.description });
      setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
      setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: config.brand });
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: config.title });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: config.description });

      let jsonLdEl = document.querySelector('script[data-group-route-jsonld="true"]');
      if (!jsonLdEl) {
        jsonLdEl = document.createElement("script");
        jsonLdEl.setAttribute("type", "application/ld+json");
        jsonLdEl.setAttribute("data-group-route-jsonld", "true");
        document.head.appendChild(jsonLdEl);
      }
      jsonLdEl.textContent = JSON.stringify(jsonLd);
    };

    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [canonical, config, jsonLd]);

  return (
    <Helmet>
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={config.brand} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={config.title} />
      <meta name="twitter:description" content={config.description} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export const GroupRouteCompactHeathrowStatus = ({ route }) => {
  const airportCode = route.statusAirportCode || "LHR";
  const airportName = route.statusAirportName || "Heathrow";
  return (
    <HeathrowLiveStatusCompact
      title={route.compactStatusTitle}
      kicker={route.compactStatusKicker || `Compact live ${airportCode} status`}
      airportCode={airportCode}
      airportName={airportName}
      fullStatusPath={route.fullStatusPath === undefined ? "/heathrow/status" : route.fullStatusPath}
      className="group-route-lhr-status"
      footnote={route.compactStatusFootnote || "Roads, flights, terminals, queues, rail and weather are checked before group airport journeys."}
    />
  );
};

export const GroupRouteDetailedHeathrowStatus = ({ config, route }) => {
  const [airport, setAirport] = useState(fallbackHeathrowLive);

  useEffect(() => {
    let cancelled = false;

    const loadHeathrowStatus = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR");
        if (cancelled) return;
        const liveHeathrow = (res?.data?.airports || []).find((item) => String(item?.code || "").toUpperCase() === "LHR");
        setAirport(liveHeathrow || fallbackHeathrowLive);
      } catch (err) {
        if (!cancelled) setAirport(fallbackHeathrowLive);
      }
    };

    loadHeathrowStatus();
    const timer = setInterval(loadHeathrowStatus, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const detailedGroups = detailedHeathrowGroups(airport);
  const vehicleLabel = config?.vehicle || "group";

  return (
    <section className="minibus-v2-lhr-detail slough-route-lhr-detail" aria-label="Detailed Heathrow status preview">
      <div className="minibus-v2-content-header minibus-v2-lhr-detail-head">
        <span className="minibus-v2-live-kicker"><i /> Detailed LHR status</span>
        <h2>{route.detailedStatusTitle ? route.detailedStatusTitle(vehicleLabel) : `Heathrow checks for ${vehicleLabel} journeys`}</h2>
        <p>{route.detailedStatusIntro || "Roads, rail, flights, terminals, queues and weather are checked before the group commits to a quote."}</p>
      </div>
      <div className="minibus-v2-lhr-detail-grid">
        {detailedGroups.map((group) => {
          const tone = statusToneKey(group.status);
          return (
            <article className={`minibus-v2-lhr-detail-card is-${tone}`} key={group.key}>
              <div className="minibus-v2-lhr-detail-card-head">
                <b>{group.icon}</b>
                <span>
                  <strong>{group.title}</strong>
                  <small>{group.subtitle}</small>
                </span>
                <em>{group.status || "Checking"}</em>
              </div>
              <div className="minibus-v2-lhr-detail-list">
                {group.items.map((item, index) => (
                  <div key={`${group.key}-${item.title}-${index}`}>
                    <span>
                      <strong>{cleanLiveText(item.title, "Status checking")}</strong>
                      <small>{cleanLiveText(item.detail, "Live check updating")}</small>
                    </span>
                    <em className={`is-${statusToneKey(item.status)}`}>{item.status || group.status || "Checking"}</em>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>
      <div className="minibus-v2-live-overall slough-route-lhr-detail-link">
        <div>
          <strong>Need the full airport board?</strong>
          <p>Open the full Heathrow status page for wider airport intelligence and live operational context.</p>
        </div>
        <Link to="/heathrow/status">
          View full Heathrow status <span>→</span>
        </Link>
      </div>
    </section>
  );
};

export const GroupRouteReviews = ({ route, vehicle }) => (
  <div className="slough-route-reviewbar slough-route-premium-reviewbar">
    <CcgPremiumReviews
      pageKey={route.path || "/"}
      ariaLabel={`${vehicle} route customer reviews and trust signals`}
      reviewsOverride={(route.reviews?.[vehicle] || []).map(([name, text]) => ({ name, text, rating: 5 }))}
    />
  </div>
);

const GroupRoutePremiumPage = ({ variant = "minibus", route, configs }) => {
  useEffect(() => {
    trackFunnelEvent("landing_view", {
      brand: variant === "coach" ? "cheapcoachgrab" : "minibusgrab",
      source: "group_route_landing",
      service_key: route?.key,
    });
  }, [route?.key, variant]);

  const config = configs?.[variant] || configs?.minibus;
  if (!config || !route) return null;

  const canonical = `${config.domain}${config.path}`;
  const heroBenefits = route.heroBenefits || defaultHeroBenefits;

  return (
    <div className={`home-page ${route.pageClassName} ${config.className}`}>
      <GroupRouteSeo config={config} route={route} canonical={canonical} />
      <LiveStrip variant={route.liveStripVariant} />

      <main className="slough-route-main">
        <section className="minibus-v2-hero slough-route-hero">
          <div className="minibus-v2-hero-bg" aria-hidden="true" />
          <div className="minibus-v2-hero-inner slough-route-hero-inner">
            <div className="minibus-v2-hero-copy slough-route-copy">
              <span className="slough-route-eyebrow">{route.heroEyebrow}</span>
              <h1>{config.h1}</h1>
              <p>{config.intro}</p>
              <div className="minibus-v2-benefits">
                {heroBenefits.map((item) => (
                  <div className="minibus-v2-benefit" key={item.title}>
                    <b>{item.icon}</b>
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.body}</small>
                    </span>
                  </div>
                ))}
              </div>
              <div className="slough-route-metrics">
                {(route.metrics || []).map((item) => (
                  <span key={item}>{item === "__SEATS__" ? config.seats : item}</span>
                ))}
              </div>
            </div>

            <div className="minibus-v2-trust-badges slough-route-trust">
              {defaultRouteTrustCards.map((card) => (
                <article key={card.title}>
                  <small>{card.eyebrow}</small>
                  <strong>{card.title}</strong>
                  <span>{card.href ? <a href={card.href} target="_blank" rel="noopener noreferrer">{card.body}</a> : card.body}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div className="minibus-v2-quote-wrap slough-route-quote">
          <GroupRouteQuoteForm config={config} route={route} />
        </div>

        <div className="minibus-v2-mobile-benefits" aria-label="Booking benefits">
          {heroBenefits.map((item) => (
            <div className="minibus-v2-benefit" key={item.title}>
              <b>{item.icon}</b>
              <span>
                <strong>{item.title}</strong>
                <small>{item.body}</small>
              </span>
            </div>
          ))}
        </div>

        <GroupRouteReviews route={route} vehicle={config.vehicle} />
        {route.showHeathrowStatus !== false ? <GroupRouteCompactHeathrowStatus route={route} /> : null}
        {route.liveSummary === "legoland" ? <LegolandMinibusLiveSummary /> : null}

        <section className="slough-route-proof">
          <header>
            <span>{route.proofEyebrow || "Trust and proof"}</span>
            <div className="slough-route-head-copy">
              <h2>{route.proofTitle || "Licensed route support before your group books"}</h2>
            </div>
          </header>
          <div className="slough-route-grid">
            {(route.proofCards || []).map((item) => (
              <article key={item.title}>
                <strong>{item.eyebrow}</strong>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="slough-route-section">
          <header>
            <span>{route.chooseEyebrow || "Why groups choose us"}</span>
            <div className="slough-route-head-copy">
              <h2>{route.chooseTitle || "Built for group journeys"}</h2>
            </div>
          </header>
          <div className="slough-route-grid slough-route-grid-4">
            {(route.chooseCards || []).map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        {route.showPricePreview !== false ? (
          <RoutePricePreview2
            routeKey={route.routeKey}
            vehicle={config.vehicle}
            limit={3}
            title={route.pricePreviewTitle ? route.pricePreviewTitle(config.vehicle) : `${config.h1} price previews`}
          />
        ) : null}
        {route.showRouteIntelligence !== false ? <RouteIntelligencePreview2 routeKey={route.routeKey} compact /> : null}

        <section className="slough-route-section">
          <header>
            <span>{route.routeOverviewEyebrow || "Route overview"}</span>
            <div className="slough-route-head-copy">
              <h2>{route.routeOverviewTitle || "Route overview"}</h2>
              {route.routeOverviewIntro ? <p>{route.routeOverviewIntro}</p> : null}
            </div>
          </header>
          <div className="slough-route-grid slough-route-grid-3">
            {(route.routeContentCards || []).map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                {(() => {
                  const links = typeof item.links === "function" ? item.links(config.vehicle) : item.links;
                  return Array.isArray(links) && links.length ? (
                  <div className="slough-route-card-links">
                    {links.map((link) => (
                      <Link to={link.to} key={link.to}>
                        {link.label} <span>→</span>
                      </Link>
                    ))}
                  </div>
                  ) : null;
                })()}
              </article>
            ))}
          </div>
        </section>

        <section className="slough-route-section">
          <header>
            <span>{route.vehicleContextEyebrow || "Vehicle context"}</span>
            <div className="slough-route-head-copy">
              <h2>{route.vehicleContextTitle ? route.vehicleContextTitle(config.brand) : `${config.brand} vehicle options`}</h2>
            </div>
          </header>
          <div className="slough-route-grid">
            {(config.fleet || []).map((item) => <article key={item}><p>{item}</p></article>)}
          </div>
        </section>

        <section className="slough-route-section">
          <header>
            <span>{route.faqEyebrow || "Route FAQ"}</span>
            <div className="slough-route-head-copy">
              <h2>{route.faqTitle || "Common questions before booking"}</h2>
            </div>
          </header>
          <div className="slough-route-grid slough-route-grid-4">
            {(route.faqItems || []).map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="slough-route-support">
          <div>
            <h2>{route.support?.title || "Need help planning this group transfer?"}</h2>
            <p>{route.support?.body || "Use the quote form first, or contact support for multi-stop, event or luggage-heavy journeys."}</p>
          </div>
          <a href={`#${route.quote.formId}`}>{config.quoteLabel}</a>
          <Link to="/contact">Contact support</Link>
        </section>
      </main>
      <FloatingSupportChat
        brand={variant === "coach" ? "cheapcoachgrab" : "minibusgrab"}
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText={route.support?.nudgeText || `Need ${config.vehicle} help?`}
      />
    </div>
  );
};

export default GroupRoutePremiumPage;
